import { Link } from 'react-router-dom';
import { TrendingUp, Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';

const footerLinks = {
  product: [
    { name: 'Features', href: '#features' },
    { name: 'How It Works', href: '#how-it-works' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Integrations', href: '#integrations' },
    { name: 'ROI Calculator', href: '#roi' },
  ],
  company: [
    { name: 'About Us', href: '#team' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Contact', href: '#demo' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '#' },
    { name: 'Terms of Service', href: '#' },
    { name: 'Security', href: '#' },
    { name: 'Compliance', href: '#' },
  ],
};

export function LandingFooter() {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#') && href !== '#') {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="relative bg-slate-900">
      <div className="container relative py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-lg bg-purple-600">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-purple-400">
                PositionIQ
              </span>
            </Link>
            <p className="text-sm text-slate-400 mb-6 max-w-sm">
              Real-time CFTC position limit compliance monitoring for commodity traders. Stop spreadsheet chaos and prevent costly breaches.
            </p>
            <div className="space-y-2 text-sm text-slate-400">
              <div className="flex items-center gap-2 hover:text-purple-400 transition-colors cursor-pointer">
                <Mail className="h-4 w-4" />
                <span>contact@positioniq.io</span>
              </div>
              <div className="flex items-center gap-2 hover:text-purple-400 transition-colors cursor-pointer">
                <Phone className="h-4 w-4" />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-2 hover:text-purple-400 transition-colors cursor-pointer">
                <MapPin className="h-4 w-4" />
                <span>New York, NY</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a
                href="#"
                className="p-2.5 rounded-lg bg-slate-800 hover:bg-purple-600 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4 text-slate-400 hover:text-white" />
              </a>
              <a
                href="#"
                className="p-2.5 rounded-lg bg-slate-800 hover:bg-purple-600 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4 text-slate-400 hover:text-white" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-sm text-slate-400 hover:text-purple-400 transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-sm text-slate-400 hover:text-purple-400 transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-sm text-slate-400 hover:text-purple-400 transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} PositionIQ. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/app/dashboard"
              className="text-sm text-slate-400 hover:text-purple-400 transition-colors"
            >
              Sign In
            </Link>
            <a
              href="#demo"
              onClick={(e) => scrollToSection(e, '#demo')}
              className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
            >
              Request Demo
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
